import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { db, auth, functions } from '@festpix/services';
import { serverTimestamp, increment } from 'firebase/firestore';

async function getCurrentUserInfo(): Promise<{ id: string; email: string; name?: string }> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Usuário não autenticado');
  }

  const userDoc = await getDoc(doc(db, 'users', user.uid));
  const userData = userDoc.data();

  return {
    id: user.uid,
    email: user.email || '',
    name: userData?.name,
  };
}

export interface CheckoutProPreference {
  success: boolean;
  preferenceId: string;
  initPoint: string;
  sandboxInitPoint: string;
  transactionId: string;
}

export interface PaymentStatus {
  success: boolean;
  status: string;
  amount: number;
}

export async function createCheckoutProPreference(
  walletId: string,
  amount: number
): Promise<CheckoutProPreference> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Usuário não autenticado');
  }

  const createPreference = httpsCallable<
    { walletId: string; amount: number },
    CheckoutProPreference
  >(functions, 'createPreference');

  try {
    const result = await createPreference({ walletId, amount });
    return result.data;
  } catch (error: unknown) {
    console.error('Error creating Checkout Pro preference:', error);
    const errorMessage = error instanceof Error ? error.message : 'Erro ao criar preferência de pagamento';
    throw new Error(errorMessage);
  }
}

export async function getPaymentStatus(transactionId: string): Promise<PaymentStatus> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Usuário não autenticado');
  }

  const checkStatus = httpsCallable<
    { transactionId: string },
    PaymentStatus
  >(functions, 'getPaymentStatus');

  try {
    const result = await checkStatus({ transactionId });
    return result.data;
  } catch (error: unknown) {
    console.error('Error checking payment status:', error);
    const errorMessage = error instanceof Error ? error.message : 'Erro ao verificar status do pagamento';
    throw new Error(errorMessage);
  }
}

export async function transferBalance(
  fromWalletId: string,
  toWalletId: string,
  amount: number
): Promise<{ success: boolean; transactionId: string }> {
  if (amount <= 0) {
    throw new Error('O valor deve ser maior que zero');
  }

  const userInfo = await getCurrentUserInfo();

  const transactionId = await runTransaction(db, async (transaction) => {
    const fromWalletRef = doc(db, 'wallets', fromWalletId);
    const toWalletRef = doc(db, 'wallets', toWalletId);

    const fromWallet = await transaction.get(fromWalletRef);
    const toWallet = await transaction.get(toWalletRef);

    if (!fromWallet.exists()) {
      throw new Error('Carteira de origem não encontrada');
    }

    if (!toWallet.exists()) {
      throw new Error('Carteira de destino não encontrada');
    }

    const fromBalance = fromWallet.data()?.balance || 0;

    if (fromBalance < amount) {
      throw new Error('Saldo insuficiente');
    }

    transaction.update(fromWalletRef, {
      balance: increment(-amount),
    });

    transaction.update(toWalletRef, {
      balance: increment(amount),
    });

    const transactionRef = doc(db, 'transactions', crypto.randomUUID());
    transaction.set(transactionRef, {
      amount,
      fromWalletId,
      toWalletId,
      timestamp: serverTimestamp(),
      type: 'transfer',
      performedBy: {
        id: userInfo.id,
        email: userInfo.email,
        name: userInfo.name,
      },
    });

    return transactionRef.id;
  });

  return { success: true, transactionId };
}
